/**
 * regulatoryKey — clé d'identité d'un calque réglementaire.
 *
 * Source UNIQUE de vérité pour matcher deux calques réglementaires qui
 * « jouent le même rôle » :
 *   - le « Rafraîchir » (re-fetch de la même fiche → on réaligne les calques) ;
 *   - le modèle .lfgr (on applique placement + mise en forme par rôle).
 *
 * La plupart des rôles sont uniques par bloc (signal_word, hazard, euh,
 * contains, nicotine, pg, vg, ufi) → la clé est simplement le rôle.
 *
 * Seuls les pictogrammes sont MULTIPLES par bloc (un calque par code GHS) et
 * doivent être discriminés → `pictogram::GHS02`.
 *
 * Module volontairement SANS dépendance (importable côté Node pour les tests,
 * et par le builder / l'inspecteur / le moteur de modèle sans cycle).
 */

/** @param {{role?:string, pictogramCode?:string}} regBlock */
export function regBlockMatchKey(regBlock) {
    if (!regBlock || !regBlock.role) return null;
    const role = regBlock.role;
    if (role === 'pictogram') return `pictogram::${regBlock.pictogramCode || ''}`;
    // Avertissement sanitaire nicotine : posé `occurrences` fois, et une fiche peut
    // en porter plusieurs. Chaque exemplaire a sa clé, sinon le dernier texte
    // écrasait les autres au rafraîchissement (#336, relecture du 19/10).
    if (role === 'health_warning' && regBlock.exemplaire) return `health_warning::${regBlock.exemplaire}`;
    return role;
}

/** Clé d'un ÉLÉMENT (calque) du store, ou null s'il n'est pas réglementaire. */
export function regLayerKey(el) {
    return el && el.regBlock ? regBlockMatchKey(el.regBlock) : null;
}

/**
 * Rôles que seules les zones `print` de FDS Studio posent (#336) : sans zones, le
 * builder ne les produit pas — la composition n'est même plus jamais lue dans la
 * fiche. Une fiche relue SANS zones (réponse par UFI, catalogue d'avant le 14/10)
 * ne prouve donc pas que ces mentions ont disparu : elle ne les sert simplement
 * pas. Les compter comme restes effaçait l'avertissement sanitaire et la
 * composition d'une étiquette nicotinée au premier « Rafraîchir » (relecture du 19/10).
 */
export const ROLES_DES_ZONES = new Set(['precaution', 'precaution_usage', 'health_warning', 'statements', 'leaflet', 'composition']);

/** Le lot entrant a-t-il été construit sur des zones ? Le builder le marque (`regBlock.zones`). */
export function lotAvecZones(calques) {
    return (calques || []).some((l) => l?.regBlock?.zones === true);
}

/** Calque existant à GARDER même si le lot entrant ne le reproduit pas : un rôle
 *  des zones, face à un lot construit sans zones. */
export function gardeFauteDeZones(el, entrantAvecZones) {
    return !entrantAvecZones && ROLES_DES_ZONES.has(el?.regBlock?.role);
}
